/**
 * A moldura das três telas de um webchat: Fluxo, Dados e Configurações.
 *
 * Existe por um motivo concreto e um estrutural.
 *
 * O concreto: carregar o documento **uma vez**. As três telas leem o mesmo store, e se cada uma
 * chamasse `load()` na montagem, trocar de aba descartaria o rascunho em memória e recarregaria do
 * Firestore — perdendo, no pior caso, os 800ms de autosave pendente.
 *
 * O estrutural: o cabeçalho é o mesmo nas três, e é ele que responde as perguntas que valem em
 * qualquer uma — como se chama, se está salvo, o que está no ar, e onde publicar.
 *
 * **Duas faixas, e não uma.** A faixa de cima era um `flex-wrap` com seis elementos e um `ml-auto`:
 * em tela estreita ela quebrava em qualquer ponto, e o botão Publicar às vezes terminava embaixo do
 * campo de nome. Separar identidade (em cima) de navegação (embaixo) é o que GitHub, Linear e Vercel
 * fazem, e resolve os dois problemas de uma vez — cada faixa tem uma função, e nenhuma precisa
 * competir por espaço com a outra.
 */

import { useEffect, useMemo } from 'react';
import { Link, NavLink, Outlet, useNavigate, useParams } from 'react-router-dom';
import { VersionMenu } from '../components/VersionMenu.js';
import { IconeConfig, IconeDados, IconeFluxo, IconeOlho, IconePublicar } from '../components/icons.js';
import { Badge, Button, Input, LinkButton, Notice } from '../components/ui.js';
import { Logo } from '../components/Logo.js';
import { useEditor } from '../store/editor.js';
import { isDemoModeLabel } from '../lib/env.js';

/**
 * Selo de salvamento.
 *
 * Ganhou o ponto colorido do `Badge`: é informação de canto de olho, que se lê sem parar de editar.
 * Sem ele, quatro estados diferentes eram quatro caixinhas de texto igualmente discretas.
 */
function SaveStatus() {
  const dirty = useEditor((state) => state.dirty);
  const saving = useEditor((state) => state.saving);
  const savedAt = useEditor((state) => state.savedAt);
  const saveError = useEditor((state) => state.saveError);

  if (saveError) return <Badge tone="danger" dot>erro ao salvar</Badge>;
  if (saving) return <Badge tone="info" dot>salvando…</Badge>;
  if (dirty) return <Badge tone="neutral" dot>alterações não salvas</Badge>;
  if (savedAt) return <Badge tone="ok" dot>rascunho salvo</Badge>;
  return null;
}

const ABAS = [
  { to: '', label: 'Fluxo', icon: IconeFluxo, end: true },
  { to: 'dados', label: 'Dados', icon: IconeDados, end: false },
  { to: 'config', label: 'Configurações', icon: IconeConfig, end: false },
];

export function WebchatLayout() {
  const { id } = useParams<{ id: string }>();
  const load = useEditor((state) => state.load);
  const reset = useEditor((state) => state.reset);
  const loading = useEditor((state) => state.loading);
  const loadError = useEditor((state) => state.loadError);
  const name = useEditor((state) => state.name);
  const setName = useEditor((state) => state.setName);
  const publish = useEditor((state) => state.publish);
  const publishing = useEditor((state) => state.publishing);
  const publishError = useEditor((state) => state.publishError);
  const publishBlockers = useEditor((state) => state.publishBlockers);
  const publishNotice = useEditor((state) => state.publishNotice);
  const publishedSnapshot = useEditor((state) => state.publishedSnapshot);
  const getBlockers = useEditor((state) => state.getBlockers);
  const hasUnpublishedChanges = useEditor((state) => state.hasUnpublishedChanges);
  const saveNow = useEditor((state) => state.saveNow);
  const nodes = useEditor((state) => state.nodes);
  const edges = useEditor((state) => state.edges);
  const appearance = useEditor((state) => state.appearance);
  const navigate = useNavigate();

  useEffect(() => {
    if (id) void load(id);
    return () => reset();
  }, [id, load, reset]);

  const blockers = useMemo(() => getBlockers(), [nodes, edges, appearance, getBlockers]);
  /*
   * Mesmas dependências dos blockers: as duas respostas mudam junto com o conteúdo do rascunho.
   * `publishedSnapshot` entra porque publicar troca o que está no ar sem mexer no rascunho.
   */
  const temMudanca = useMemo(
    () => hasUnpublishedChanges(),
    [nodes, edges, appearance, publishedSnapshot, hasUnpublishedChanges],
  );

  if (loading) return <div className="p-8 text-sm text-slate-500">Carregando…</div>;
  if (loadError) {
    return (
      <div className="space-y-3 p-8">
        <Notice tone="danger" title="Não foi possível abrir este webchat">
          {loadError}
        </Notice>
        <Link to="/" className="text-sm text-slate-700 underline">
          voltar para a lista
        </Link>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col bg-slate-100">
      <header className="shrink-0 border-b border-slate-200 bg-white">
        {/* Faixa 1 — quem é este webchat e o que se faz com ele. */}
        <div className="flex flex-wrap items-center gap-3 px-4 py-3">
          {/* A marca é o caminho de volta, como em qualquer aplicação — daí ela viver dentro do link. */}
          <Link
            to="/"
            title="Voltar para a lista de webchats"
            className="inline-flex items-center gap-2 rounded-lg text-sm text-slate-500 transition hover:text-slate-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
          >
            <Logo size={22} />
            Webchats
          </Link>
          <Input
            value={name}
            aria-label="Nome do webchat"
            onChange={(event) => setName(event.target.value)}
            className="max-w-xs font-semibold"
          />
          <SaveStatus />
          {/* O selo de publicação é o menu de histórico — mesma área, com a opção de restaurar. */}
          <VersionMenu />
          {isDemoModeLabel ? <Badge tone="warn">{isDemoModeLabel}</Badge> : null}

          <div className="ml-auto flex items-center gap-2">
            {/*
              Link de verdade para Ctrl/Cmd+clique e botão do meio abrirem em outra aba — é assim que
              se edita de um lado e observa do outro. No clique simples, o autosave pendente é
              descarregado antes de navegar, senão os últimos 800ms de edição ficariam de fora.
            */}
            <LinkButton
              to={`/w/${id}/preview`}
              title="Ctrl+clique abre numa nova aba"
              onClick={(event) => {
                if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) {
                  void saveNow();
                  return;
                }
                event.preventDefault();
                void saveNow().then(() => navigate(`/w/${id}/preview`));
              }}
            >
              <IconeOlho />
              Preview
            </LinkButton>
            {/*
              Desabilitado quando o rascunho é igual ao que está no ar: publicar não criaria versão, e
              deixar clicável convidava a encher o histórico de entradas idênticas — com v5 a v9 iguais,
              "restaurar a v7" não quer dizer nada. O servidor também recusa, então isto é conveniência,
              não a garantia.
            */}
            <Button
              variant="primary"
              disabled={publishing || blockers.length > 0 || !temMudanca}
              /*
                A terceira mensagem responde uma confusão relatada: quem acrescenta um domínio vem
                procurar o Publicar e o encontra apagado. Dizer só "o rascunho já é igual ao que está
                no ar" é verdade e não ajuda — publicar governa **aparência e fluxo**, e domínio,
                segredo e limite não passam por versão nenhuma. Ver `Propagacao.tsx`.
              */
              title={
                blockers.length
                  ? 'Corrija os problemas antes de publicar'
                  : temMudanca
                    ? 'Promove o rascunho para o ar'
                    : 'A aparência e o fluxo já estão no ar. Domínios, segredos e limite não passam por publicação — valem no próximo salvamento.'
              }
              onClick={() => void publish()}
            >
              {publishing ? null : <IconePublicar />}
              {publishing ? 'Publicando…' : 'Publicar'}
            </Button>
          </div>
        </div>

        {/* Faixa 2 — as três telas. Rotas de verdade: cada uma tem URL, back e Ctrl+clique. */}
        <nav aria-label="Seções do webchat" className="flex gap-1 px-2">
          {ABAS.map((aba) => (
            <NavLink
              key={aba.label}
              to={aba.to ? `/w/${id}/${aba.to}` : `/w/${id}`}
              end={aba.end}
              className={({ isActive }) =>
                `inline-flex items-center gap-2 border-b-2 px-3 py-3 text-sm font-medium transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 ${
                  isActive
                    ? 'border-slate-900 text-slate-900'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`
              }
            >
              <aba.icon size={15} />
              {aba.label}
            </NavLink>
          ))}
        </nav>
      </header>

      {/*
        Os avisos ficam entre o cabeçalho e o conteúdo, valendo para as três telas: um bloqueio de
        publicação é do webchat, não da tela em que se está. Ficam fora da área de rolagem para não
        sumirem quando se rola a tela de configurações.
      */}
      {publishNotice && !publishError ? (
        <div className="shrink-0 border-b border-slate-200 bg-white px-4 py-3">
          <Notice tone="ok">{publishNotice}</Notice>
        </div>
      ) : null}

      {blockers.length > 0 || publishError ? (
        <div className="shrink-0 border-b border-slate-200 bg-white px-4 py-3">
          <Notice
            tone={publishError ? 'danger' : 'warn'}
            title={publishError ?? 'Ainda não é possível publicar'}
          >
            <ul className="list-disc space-y-1 pl-4">
              {(publishBlockers.length ? publishBlockers : blockers).map((blocker) => (
                <li key={blocker}>{blocker}</li>
              ))}
            </ul>
          </Notice>
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1 flex-col">
        <Outlet />
      </div>
    </div>
  );
}
